import { useEffect, useState } from 'react'
import { useStandaloneMode } from './useStandaloneMode'

/**
 * Banner informativo para iOS Safari (§3.3 SRS — REQ-PWA-003).
 *
 * Se muestra cuando:
 *   - El UA es Safari iOS (iPhone, iPad).
 *   - La app NO está corriendo en modo standalone.
 *   - El usuario no lo ha descartado previamente.
 *
 * iOS no tiene un evento `beforeinstallprompt`; la instalación es manual desde el menú
 * "Compartir → Agregar a pantalla de inicio". Este banner guía visualmente al usuario.
 */

const STORAGE_KEY = 'intrepid.ios_banner_dismissed'

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const isIos = /iPad|iPhone|iPod/.test(ua)
  // En iOS, Chrome, Firefox, Edge etc usan WebKit con prefijos CriOS/FxiOS/EdgiOS.
  // Solo Safari nativo soporta "Agregar a inicio".
  const isSafariNative = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)
  return isIos && isSafariNative
}

export function IOSInstallBanner() {
  const isStandalone = useStandaloneMode()
  const [dismissed, setDismissed] = useState<boolean>(
    () => typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY) === '1',
  )
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (isStandalone || dismissed) return
    if (!isIosSafari()) return
    // Pequeño delay para no pintar el banner sobre el primer render del login
    const t = setTimeout(() => setShow(true), 1200)
    return () => clearTimeout(t)
  }, [isStandalone, dismissed])

  if (!show || isStandalone || dismissed) return null

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1')
    } catch {
      /* localStorage puede fallar en modo privado */
    }
    setDismissed(true)
  }

  return (
    <div className="ios-banner" role="dialog" aria-label="Instalar como aplicación">
      <button className="ios-banner__close" onClick={handleDismiss} aria-label="Cerrar">
        ✕
      </button>
      <p className="ios-banner__title">Instala Velt Intrepid en tu inicio</p>
      <p className="ios-banner__body">
        Toca <strong>Compartir</strong> <span aria-hidden="true">⎘</span> y elige{' '}
        <strong>Agregar a pantalla de inicio</strong> para usar la app como nativa.
      </p>
    </div>
  )
}
